import React, { useEffect, useRef, useState } from "react";
import { BookMarked, Send, X } from "lucide-react";
import { API } from "../api";

const QUICK_PROMPTS = ["Polecisz dobry kryminał?", "Jak działa dostawa?", "Jakie są formy płatności?", "Szukam audiobooka na prezent"];

export function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Cześć! Jestem Libi, Twoja księgowa AI. Pomogę Ci znaleźć idealną książkę lub odpowiem na pytania o sklep. O czym myślisz?" },
  ]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const sessionRef = useRef(localStorage.getItem("dl_chat_session") || (() => {
    const id = crypto.randomUUID();
    localStorage.setItem("dl_chat_session", id);
    return id;
  })());
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typing, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || typing) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setTyping(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, session_id: sessionRef.current }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      let buffer = "";
      setMessages((m) => [...m, { role: "assistant", text: "" }]);
      setTyping(false);
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const { delta } = JSON.parse(payload);
            acc += delta;
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", text: acc };
              return copy;
            });
          } catch {}
        }
      }
    } catch {
      setTyping(false);
      setMessages((m) => [...m, { role: "assistant", text: "Ups, coś poszło nie tak. Spróbuj ponownie za chwilę." }]);
    }
  };

  return (
    <>
      <button
        data-testid="chat-open-button"
        onClick={() => setOpen(!open)}
        className={`fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-2xl shadow-cyan-500/40 transition-transform duration-200 hover:scale-110 active:scale-95 ${open ? "" : "chat-pulse"}`}
        aria-label="Otwórz czat"
      >
        {open ? <X className="h-6 w-6" /> : <BookMarked className="h-6 w-6" />}
      </button>

      {open && (
        <div data-testid="chat-panel" className="fixed bottom-24 right-4 z-50 flex h-[480px] w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-2xl glass-panel shadow-2xl shadow-cyan-500/20 sm:right-6">
          <div className="flex items-center gap-3 border-b border-cyan-500/15 bg-slate-900/80 px-4 py-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600">
              <BookMarked className="h-4 w-4 text-white" />
            </div>
            <div>
              <div className="text-sm font-bold text-slate-100">Libi — asystentka DigiLib</div>
              <div className="flex items-center gap-1.5 text-[11px] text-emerald-400">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> online
              </div>
            </div>
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "rounded-br-sm bg-gradient-to-r from-cyan-500 to-blue-600 text-white"
                    : "rounded-bl-sm bg-slate-800/90 text-slate-200"
                }`}>
                  {m.text}
                </div>
              </div>
            ))}
            {typing && (
              <div className="flex justify-start" data-testid="chat-typing-indicator">
                <div className="flex items-center gap-1.5 rounded-2xl rounded-bl-sm bg-slate-800/90 px-4 py-3">
                  <span className="typing-dot h-2 w-2 rounded-full bg-cyan-400" />
                  <span className="typing-dot h-2 w-2 rounded-full bg-cyan-400" />
                  <span className="typing-dot h-2 w-2 rounded-full bg-cyan-400" />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {messages.length <= 1 && (
            <div className="flex flex-wrap gap-2 px-4 pb-2">
              {QUICK_PROMPTS.map((q) => (
                <button key={q} onClick={() => send(q)} className="rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1.5 text-[11px] text-cyan-300 transition-colors hover:bg-cyan-500/20">
                  {q}
                </button>
              ))}
            </div>
          )}

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="flex items-center gap-2 border-t border-cyan-500/15 bg-slate-900/80 p-3"
          >
            <input
              data-testid="chat-message-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Napisz wiadomość..."
              className="w-full rounded-xl border border-slate-700 bg-slate-800/80 px-3.5 py-2.5 text-sm text-slate-200 placeholder-slate-500 outline-none transition-colors focus:border-cyan-500"
            />
            <button
              data-testid="chat-send-button"
              type="submit"
              disabled={typing || !input.trim()}
              className="rounded-xl bg-gradient-to-r from-cyan-500 to-teal-500 p-2.5 text-slate-950 transition-transform hover:scale-105 active:scale-95 disabled:opacity-40"
              aria-label="Wyślij"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
